import React, { useCallback, useEffect, useState } from 'react';
import forumI18n, { usernameNotice, replyPermissionNotice } from '../services/forumI18n';
import api, { getMember } from '../services/api';
import ForumRichText, { ForumContent, contentText } from './ForumRichText';
import forumEditorI18n from '../services/forumEditorI18n';
const servicePaths=['/bazi/','/bazi/compatibility/','/bazi/date-selection/','/wts/'];
const serviceNames={
 'zh-TW':['綜合命理','情侶配對','擇日','黃大仙靈籤'],'zh-CN':['综合命理','情侣配对','择日','黄大仙灵签'],
 en:['Integrated Destiny','Couple Match','Auspicious Dates','Wong Tai Sin Fortune Sticks'],
 ko:['종합 명리','커플 궁합','택일','웡타이신 운세 제비'],ja:['総合命理','恋人相性','吉日選び','黄大仙のおみくじ'],
 ms:['Metafizik Bersepadu','Padanan Pasangan','Tarikh Bertuah','Ramalan Wong Tai Sin'],
 es:['Destino integrado','Compatibilidad de pareja','Fechas propicias','Oráculo de Wong Tai Sin']
};

const topics = [
  ['family-love', '家庭與感情', 'Family and Love', '家庭相處、姻緣與感情疑問'],
  ['wealth', '財運', 'Wealth', '財運、理財方向與生活規劃'],
  ['jobs', '事業與工作', 'Jobs', '求職、轉工與事業發展'],
  ['others', '其他', 'Others', '其他風水與命理疑問']
];

export default function ForumPage({ lang = 'zh-TW' }) {
  const c = forumI18n(lang);
  const name = useCallback(t => c[topics.findIndex(x => x[0] === t[0]) + 1], [c]);
  const parts = window.location.pathname.replace(/\/+$/, '').split('/');
  const composing = parts[4] === 'new';
  const [selectedBoard, setSelectedBoard] = useState(parts[3] || 'family-love');
  const slug = composing ? selectedBoard : (parts[3] || 'family-love');
  const topic = topics.find(t => t[0] === slug);
  const questionId = composing ? null : parts[4];
  const e = forumEditorI18n(lang);
  const [member, setMember] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [page, setPage] = useState(() => Math.max(1, Number.parseInt(new URLSearchParams(window.location.search).get('page'),10)||1));
  const base = `/forum/${slug}/questions`;

  useEffect(() => {
    let active = true;
    getMember().then(async result => {
      if (!result.authenticated) return;
      const profile = (await api.get('/forum/profile')).data;
      if (active) setMember({ nickname: profile.nickname });
    })
      .catch(() => { if (active) setError(c[6]); })
      .finally(() => { if (active) setAuthReady(true); });
    return () => { active = false; };
  }, [c]);

  useEffect(() => {
    if (!topic || composing) return;
    let active = true;
    setData(null);
    api.get(questionId ? `${base}/${questionId}` : base, { params: { page, lang } })
      .then(r => { if (active) setData(r.data); })
      .catch(() => { if (active) setError(c[7]); });
    return () => { active = false; };
  }, [base, questionId, page, topic, lang, c, composing]);

  useEffect(() => { setError(''); }, [lang]);

  useEffect(() => {
    document.title = `${topic ? `${name(topic)} | ` : ''}${c[0]} | Seeable.Life`;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = `https://seeable.life${window.location.pathname}${page>1?`?page=${page}`:''}`;
  }, [topic, lang, c, name, page]);

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const result = await api.post(questionId ? `${base}/${questionId}/replies` : base, questionId ? { body } : { title, body, body_format: 'html', language: lang });
      if (!questionId) window.location.assign(`/bazi/forum/${slug}/${result.data.id}/`);
      else { setData((await api.get(`${base}/${questionId}`, { params: { lang } })).data); setBody(''); }
    } catch (err) {
      setError(err.response?.status === 401 ? c[8] : c[9]);
    } finally { setBusy(false); }
  }

  const stamp = value => new Date(value).toLocaleString(lang);
  return <section data-theme="light" className="p-4 md:p-8 bg-base-100 text-base-content rounded-box">
    <header className="flex items-start justify-between gap-4 mb-6">
      <div><a className="link text-sm" href="/bazi/forum/">{c[0]}</a><h1 className="text-3xl font-bold my-3">{composing ? e.create : c[0]}</h1><p className="text-base-content/70">{e.intro}</p></div>
      {!composing && topic && <a className="btn shrink-0" href={`/bazi/forum/${slug}/new/`}>＋ {e.create}</a>}
    </header>
    {error && <p role="alert" className="text-error my-4">{error}</p>}
    {slug && !topic && <p>{c[12]}<a className="link" href="/bazi/forum/">{c[13]}</a></p>}
    {topic && <>
      <nav aria-label={c[14]} className="flex flex-wrap gap-2 p-3 bg-base-200 rounded-box mb-8">{topics.map(t => <a className={`btn ${t[0] === slug ? 'border-base-content' : 'btn-ghost'}`} aria-current={t[0] === slug ? 'page' : undefined} key={t[0]} href={`/bazi/forum/${t[0]}/`}>{name(t)}</a>)}</nav>
      {composing && <a className="link inline-block mb-4" href={`/bazi/forum/${slug}/`}>{c[16]}</a>}
      {!composing && !data && !error && <p role="status">{c[15]}</p>}
      {data?.question && <>
        <a className="link" href={`/bazi/forum/${slug}/`}>{c[16]}</a>
        <article className="card card-border my-4"><div className="card-body"><h2 className="card-title">{data.question.title}</h2><p className="text-sm text-base-content/60">{data.question.author} · {stamp(data.question.created_at)}</p><ForumContent body={data.question.body} format={data.question.body_format} /></div></article>
        <h2 className="text-xl font-bold">{c[17]}（{data.replies.length}）</h2>
        {data.replies.map(r => <article className="card card-border my-4" key={r.id}><div className="card-body"><p>{r.display_name || r.author} · {stamp(r.created_at)}</p><p className="whitespace-pre-wrap break-words">{r.body}</p>{['/bazi/', '/bazi/compatibility/', '/bazi/date-selection/', '/wts/'].includes(r.service_path) && <a className="btn" href={r.service_path}>{(serviceNames[lang] || serviceNames.en)[servicePaths.indexOf(r.service_path)]}</a>}</div></article>)}
        {!data.replies.length && <p className="my-4">{c[18]}</p>}
      </>}
      {data?.questions && <>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4"><h2 className="text-xl font-bold">{name(topic)} · {c[19]}</h2><span className="text-sm text-base-content/60">({data.total})</span></div>
        {!data.questions.length && <p className="my-4">{c[20]}</p>}
        <div className="grid gap-3 mb-6">{data.questions.map(q => <article className="card card-border hover:bg-base-200 transition-colors" key={q.id}><div className="card-body gap-3"><div className="flex justify-between items-start gap-4"><h3 className="card-title"><a className="link no-underline hover:underline" href={`/bazi/forum/${slug}/${q.id}/`}>{q.title}</a></h3><span className="shrink-0 text-sm text-base-content/60">💬 {q.reply_count}</span></div><p className="text-base-content/70 leading-relaxed break-words">{contentText(q.body,q.body_format).slice(0,140)}</p><p className="text-sm text-base-content/60">{q.author} · {stamp(q.created_at)}</p></div></article>)}</div>
        <div className="flex gap-3 items-center"><button className="btn" disabled={page === 1} onClick={() => setPage(p => p-1)}>{c[21]}</button><span>{c[23]} {page}</span><button className="btn" disabled={page*20 >= data.total} onClick={() => setPage(p => p+1)}>{c[22]}</button></div>
      </>}
      {authReady && !member && <p className="my-6">{c[24]}<a className="btn" href={`/bazi/api/auth/google/login?next=${encodeURIComponent(window.location.pathname)}`}>{c[25]}</a></p>}
      {member && (composing || questionId) && <p className="my-6">{usernameNotice[lang] || usernameNotice.en} <strong>{member.nickname}</strong></p>}
      {member && data && questionId && !data.can_reply && <p className="my-6">{replyPermissionNotice[lang] || replyPermissionNotice.en}</p>}
      {member && (composing || (questionId && data?.can_reply)) && <form onSubmit={submit} className="grid gap-5 my-8 max-w-3xl">
        <h2 className="text-xl font-bold">{questionId ? c[30] : e.create}</h2>
        <p>{c[32]}: {member.nickname}</p>
        {composing && <><p className="text-base-content/70">{e.hint}</p><label className="grid gap-2">{e.board}<select className="select w-full" value={selectedBoard} onChange={event=>setSelectedBoard(event.target.value)}>{topics.map(t=><option key={t[0]} value={t[0]}>{name(t)}</option>)}</select></label><label className="grid gap-2">{c[33]}<input className="input w-full" required maxLength={160} value={title} onChange={event => setTitle(event.target.value)} /></label><ForumRichText label={c[35]} labels={e} onChange={setBody} /></>}
        {questionId && <label>{c[34]}<textarea className="textarea w-full min-h-40" required maxLength={10000} value={body} onChange={event => setBody(event.target.value)} /></label>}
        <button className="btn justify-self-start" disabled={busy || !contentText(body,composing?'html':'plain').trim() || body.length>10000 || (!questionId && !title.trim())}>{busy ? c[36] : questionId ? c[30] : c[37]}</button>
      </form>}
    </>}
  </section>;
}
